/**
 * Document upload: presign → PUT bytes to S3 → keep fileUrl.
 * View uses POST /files/download-url → short-lived downloadUrl (private S3).
 */
import axios from 'axios';
import api from './httpClient';

const ALLOWED_TYPES = new Set([
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/jpg',
]);

/** Reject files that are the wrong type or too large (default 5MB UI limit). */
export function validateUploadFile(file, maxBytes = 5 * 1024 * 1024) {
  if (!file) return { ok: false, error: 'No file selected' };
  const type = (file.type || 'application/pdf').toLowerCase();
  if (!ALLOWED_TYPES.has(type)) {
    return { ok: false, error: 'Invalid file type. Allowed: PDF, JPEG, PNG' };
  }
  if (file.size > maxBytes) {
    return { ok: false, error: 'File size must be less than 5MB' };
  }
  return { ok: true, error: null };
}

/** Ask our backend for a one-time S3 uploadUrl (backend holds DOCUMENT_API_TOKEN). */
export async function requestPresign(file, documentType) {
  const { data } = await api.post('/upload/presign', {
    fileName: file.name || `${documentType || 'document'}.pdf`,
    fileType: file.type || 'application/pdf',
    size: file.size,
    document_type: documentType || null,
  });
  if (!data?.uploadUrl || !data?.fileUrl) {
    throw new Error('Invalid presign response from server');
  }
  return data;
}

/** PUT raw file bytes straight to S3 using the presigned URL. */
export async function putFileToS3(uploadUrl, file) {
  await axios.put(uploadUrl, file, {
    headers: { 'Content-Type': file.type || 'application/pdf' },
  });
}

/**
 * Full upload flow used by registration / dashboard.
 * Returns the same shape callers already expect (file_path = S3 fileUrl).
 */
export async function uploadFileViaPresign(file, documentType) {
  const check = validateUploadFile(file);
  if (!check.ok) {
    const err = new Error(check.error);
    err.response = { data: { detail: check.error } };
    throw err;
  }

  const presign = await requestPresign(file, documentType);
  await putFileToS3(presign.uploadUrl, file);

  return {
    file_path: presign.fileUrl,
    file_name: file.name,
    file_id: presign.fileId || null,
    document_type: documentType,
  };
}

/**
 * Build a URL the browser can open for legacy local filenames only.
 * Do not use this for https fileUrl — those need openUploadedFile().
 */
export function resolveFileViewUrl(filePath) {
  if (!filePath) return null;
  if (/^https?:\/\//i.test(filePath)) return filePath;
  const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
  const token = localStorage.getItem('token') || '';
  return `${baseUrl}/api/files/${encodeURIComponent(filePath)}?token=${encodeURIComponent(token)}`;
}

/**
 * Mint a short-lived GET URL for a private S3/CDN fileUrl (Document API download).
 */
export async function requestDownloadUrl(fileUrl) {
  const { data } = await api.post('/files/download-url', { url: fileUrl });
  const downloadUrl = data?.downloadUrl;
  if (!downloadUrl) {
    throw new Error('Invalid download response from server');
  }
  return downloadUrl;
}

/**
 * Open an uploaded document in a new tab.
 * - https fileUrl → backend download pre-sign (private buckets need this)
 * - legacy local name → /api/files/...?token=
 */
export async function openUploadedFile(filePath) {
  if (!filePath) {
    throw new Error('No file to open');
  }
  if (/^https?:\/\//i.test(filePath)) {
    const downloadUrl = await requestDownloadUrl(filePath);
    window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    return;
  }
  const localUrl = resolveFileViewUrl(filePath);
  window.open(localUrl, '_blank', 'noopener,noreferrer');
}
